import { useState, useCallback } from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';

interface ImageCarouselProps {
  images: string[];
  venueName: string;
  onImageClick?: (index: number) => void;
}

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 300,
  backgroundColor: theme.palette.grey[100],
  overflow: 'hidden',
  borderRadius: 0,
  [theme.breakpoints.up('md')]: {
    height: 400,
    borderRadius: 16,
  },
}));

const ImageWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  cursor: 'pointer',
});

const CarouselImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const PlaceholderContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  backgroundColor: theme.palette.grey[100],
}));

const PlaceholderIcon = styled(ImageOutlinedIcon)(({ theme }) => ({
  fontSize: 80,
  color: theme.palette.grey[400],
}));

const PlaceholderText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '1rem',
  color: theme.palette.grey[500],
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(4px)',
  border: `1px solid ${alpha(theme.palette.common.black, 0.1)}`,
  color: theme.palette.text.primary,
  width: 44,
  height: 44,
  opacity: 0,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'translateY(-50%) scale(1.05)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    color: theme.palette.text.disabled,
  },
  '.carousel-container:hover &': {
    opacity: 1,
  },
}));

const PrevButton = styled(NavigationButton)({
  left: 16,
});

const NextButton = styled(NavigationButton)({
  right: 16,
});

const DotsContainer = styled(Box)({
  position: 'absolute',
  bottom: 16,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 8,
  padding: '8px 12px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(8px)',
  borderRadius: 20,
});

const Dot = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
  transform: isActive ? 'scale(1.2)' : 'scale(1)',
  '&:hover': {
    backgroundColor: '#ffffff',
  },
}));

const ImageCounter = styled(Box)({
  position: 'absolute',
  top: 16,
  right: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(8px)',
  borderRadius: 8,
  color: '#ffffff',
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.875rem',
  fontWeight: 500,
});

const FullscreenButton = styled(IconButton)({
  position: 'absolute',
  bottom: 16,
  right: 16,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(8px)',
  color: '#ffffff',
  width: 40,
  height: 40,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    transform: 'scale(1.05)',
  },
});

export function ImageCarousel({ images, venueName, onImageClick }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());

  const hasImages = images.length > 0;
  const showNavigation = images.length > 1;

  const handleImageError = useCallback((index: number) => {
    setFailedImages((prev) => new Set(prev).add(index));
  }, []);

  const isCurrentImageFailed = failedImages.has(currentIndex);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };

  const handleImageClick = () => {
    if (onImageClick && hasImages) {
      onImageClick(currentIndex);
    }
  };

  const handleFullscreenClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onImageClick && hasImages) {
      onImageClick(currentIndex);
    }
  };

  if (!hasImages) {
    return (
      <CarouselContainer>
        <PlaceholderContainer>
          <PlaceholderIcon />
          <PlaceholderText>No images available</PlaceholderText>
        </PlaceholderContainer>
      </CarouselContainer>
    );
  }

  return (
    <CarouselContainer className="carousel-container">
      <ImageWrapper onClick={handleImageClick}>
        {isCurrentImageFailed ? (
          <PlaceholderContainer>
            <BrokenImageIcon sx={{ fontSize: 60, color: 'grey.400' }} />
            <PlaceholderText>Image unavailable</PlaceholderText>
          </PlaceholderContainer>
        ) : (
          <CarouselImage
            src={images[currentIndex]}
            alt={`${venueName} - Image ${currentIndex + 1}`}
            loading="lazy"
            onError={() => handleImageError(currentIndex)}
          />
        )}
      </ImageWrapper>

      {/* Navigation Arrows */}
      {showNavigation && (
        <>
          <PrevButton onClick={handlePrev} aria-label="Previous image">
            <ChevronLeftIcon />
          </PrevButton>
          <NextButton onClick={handleNext} aria-label="Next image">
            <ChevronRightIcon />
          </NextButton>
        </>
      )}

      {/* Image Counter */}
      <ImageCounter>
        {currentIndex + 1} / {images.length}
      </ImageCounter>

      {/* Fullscreen Button */}
      {onImageClick && (
        <FullscreenButton onClick={handleFullscreenClick} aria-label="View fullscreen">
          <FullscreenIcon />
        </FullscreenButton>
      )}

      {/* Dot Indicators */}
      {showNavigation && images.length <= 10 && (
        <DotsContainer>
          {images.map((_, index) => (
            <Dot
              key={index}
              isActive={index === currentIndex}
              onClick={(e) => handleDotClick(e, index)}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </DotsContainer>
      )}
    </CarouselContainer>
  );
}
